"use client";

/**
 * @file ページ遷移中に画面最上部へ進捗バーを表示する常駐 provider。
 * クリック直後に描画が変わらない画面で「操作を受け付けたか分からない」問題を、
 * 遷移が始まった時点のフィードバックで解消する。
 */

import { ProgressProvider } from "@bprogress/next/app";

/** バーの太さ。header の下端に重ならない程度に細く保つ。 */
const BAR_HEIGHT = "3px";

/**
 * 表示までの待ち時間 (ms)。prefetch 済みで即完了する遷移では
 * バーが一瞬だけ光る不快なちらつきになるため、遅い遷移だけ拾う。
 */
const START_DELAY_MS = 120;

type Props = {
  children: React.ReactNode;
  /**
   * CSP の nonce。provider は runtime に `<style>` を挿入するため、
   * middleware が発行した nonce をそのまま引き継ぐ。
   */
  nonce?: string;
};

/**
 * アプリ全体の遷移進捗バーを提供する。
 * anchor (`next/link` を含む) クリック起点の遷移を検知し、遷移完了で自動的に閉じる。
 * nuqs などの shallow routing では表示しない（サーバー往復がなく待ち時間もないため）。
 * @param children provider 配下に描画する要素。
 * @param nonce middleware が発行した CSP nonce。
 * @returns 進捗バー付きの provider 要素。
 */
export function NavigationProgressProvider({ children, nonce }: Props) {
  return (
    <ProgressProvider
      color="var(--primary)"
      height={BAR_HEIGHT}
      nonce={nonce}
      delay={START_DELAY_MS}
      startPosition={0.1}
      shallowRouting
      options={{ showSpinner: false }}
    >
      {children}
    </ProgressProvider>
  );
}
